import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { AWAY_WINDOW_CHANGED_EVENT, getMyAway, type AwayWindow } from '../api/away';
import { useAuth } from './AuthContext';
import { isWindowVisible } from '../utils/windowVisibility';

/**
 * Slow safety net only. The quiet-mode flag is otherwise event-driven: the
 * user's own away/DND system frames on the shared socket, the local
 * AWAY_WINDOW_CHANGED_EVENT, window focus, and a timer armed for the exact
 * end of an active window. (This replaced an unconditional 30-second poll of
 * /away/me that ran for the app's whole lifetime, tray or not.)
 */
export const DND_QUIET_SAFETY_NET_MS = 5 * 60_000;

type DndQuietModeApi = {
  isDndQuiet: boolean;
  refreshDndQuietMode: () => Promise<boolean>;
};

const DEFAULT_VALUE: DndQuietModeApi = {
  isDndQuiet: false,
  refreshDndQuietMode: async () => false,
};

const DndQuietModeContext = createContext<DndQuietModeApi>(DEFAULT_VALUE);

export function isActiveDndWindow(window: AwayWindow | null, now = Date.now()): boolean {
  if (!window || window.status_type !== 'dnd' || window.ended_at) return false;
  const startsAt = Date.parse(window.starts_at);
  const endsAt = Date.parse(window.ends_at);
  return Number.isFinite(startsAt) && Number.isFinite(endsAt) && startsAt <= now && now < endsAt;
}

/**
 * Milliseconds until the DND window's next state change — its START when it is
 * still scheduled, its END once it is running — or null when there is nothing
 * left to wait for.
 *
 * Both edges matter. The server announces a DND window with an `away_start`
 * system message at CREATION time, not when the window actually begins, and
 * the sweeper that starts it emits only a presence frame. So nothing pushes
 * "your DND just started" to this client; without a timer for the start edge,
 * a window booked in advance would never turn quiet mode on and calls would
 * ring straight through it.
 */
export function msUntilNextDndBoundary(window: AwayWindow | null, now = Date.now()): number | null {
  if (!window || window.status_type !== 'dnd' || window.ended_at) return null;
  const startsAt = Date.parse(window.starts_at);
  const endsAt = Date.parse(window.ends_at);
  if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt)) return null;
  if (now < startsAt) return startsAt - now;
  if (now < endsAt) return endsAt - now;
  return null;
}

/**
 * A client-side fail-safe for a real DND window. The server remains
 * authoritative for delivery, while this prevents any locally delivered frame
 * from creating sound, a native alert, or an incoming-call screen.
 */
export function DndQuietModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isDndQuiet, setIsDndQuiet] = useState(false);
  // The user's own DND window, whether it is running yet or not — a scheduled
  // one still needs a timer for the moment it starts.
  const [dndWindow, setDndWindow] = useState<AwayWindow | null>(null);

  // The server announces an away/DND window with one system message PER
  // conversation the user belongs to, and every one of them asks for a
  // refresh. Without this, toggling DND in a 40-conversation account fired 40
  // simultaneous (uncacheable) GET /away/me requests. Callers that arrive
  // while a refresh is in flight share its result instead.
  const inFlightRef = useRef<Promise<boolean> | null>(null);

  const refreshDndQuietMode = useCallback((): Promise<boolean> => {
    if (inFlightRef.current) return inFlightRef.current;
    const run = (async (): Promise<boolean> => {
    if (!user?.id) {
      setIsDndQuiet(false);
      setDndWindow(null);
      return false;
    }
    try {
      const window = await getMyAway();
      const next = isActiveDndWindow(window);
      setIsDndQuiet(next);
      // Kept even when it has not started: the boundary timer below needs it.
      setDndWindow(window && window.status_type === 'dnd' && !window.ended_at ? window : null);
      return next;
    } catch {
      // Server-side call and mention guards remain the source of truth when a
      // refresh is temporarily unavailable.
      setIsDndQuiet(false);
      setDndWindow(null);
      return false;
    }
    })();
    inFlightRef.current = run;
    return run.finally(() => { inFlightRef.current = null; });
  }, [user?.id]);

  useEffect(() => {
    void refreshDndQuietMode();
    const onFocus = () => { void refreshDndQuietMode(); };
    const onAwayWindowChange = () => { void refreshDndQuietMode(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener(AWAY_WINDOW_CHANGED_EVENT, onAwayWindowChange);
    const safetyNet = window.setInterval(() => {
      if (document.visibilityState === 'hidden' || !isWindowVisible()) return;
      void refreshDndQuietMode();
    }, DND_QUIET_SAFETY_NET_MS);
    return () => {
      window.clearInterval(safetyNet);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(AWAY_WINDOW_CHANGED_EVENT, onAwayWindowChange);
    };
  }, [refreshDndQuietMode]);

  // Flip quiet mode on at the window's start and off at its end, without
  // polling for either. Re-running after each refresh re-arms for the far edge.
  useEffect(() => {
    const ms = msUntilNextDndBoundary(dndWindow);
    if (ms === null) return;
    const timer = window.setTimeout(() => { void refreshDndQuietMode(); }, ms + 250);
    return () => window.clearTimeout(timer);
  }, [dndWindow, isDndQuiet, refreshDndQuietMode]);

  const value = useMemo(() => ({ isDndQuiet, refreshDndQuietMode }), [isDndQuiet, refreshDndQuietMode]);
  return <DndQuietModeContext.Provider value={value}>{children}</DndQuietModeContext.Provider>;
}

export function useDndQuietMode(): DndQuietModeApi {
  return useContext(DndQuietModeContext);
}
